import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react'
import { X, Search } from 'lucide-react'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { integrationService, type EmployeeData } from '@/services/api/integrationService'
import { talentReviewService } from '@/services/api/talentReviewService'

interface Employee {
  id: string
  name: string
  role: string
  department: string
  campus?: string
  email?: string
}

interface EmployeePickerProps {
  onSelectionChange?: (employees: Employee[]) => void
  selectedEmployees?: Employee[]
  placeholder?: string
  debounceMs?: number
  excludeUserId?: string
  /** Where to look: the F&O-backed employee search (default), or the platform
      directory the Annual I&G Review reads — instant, and it falls back to the
      F&O search if it fails (HR administrator, 8 Sep 2026: an HRBP's name found nobody in the room). */
  source?: 'fo' | 'platform'
}

export const EmployeePicker: React.FC<EmployeePickerProps> = ({
  onSelectionChange,
  selectedEmployees: controlledSelected = [],
  placeholder = 'Search team members by name, role, or function...',
  debounceMs = 300,
  excludeUserId,
  source = 'fo',
}) => {
  const [searchQuery, setSearchQuery] = useState('')
  const [debouncedQuery, setDebouncedQuery] = useState('')
  const [suggestions, setSuggestions] = useState<Employee[]>([])
  const [isOpen, setIsOpen] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [highlightedIndex, setHighlightedIndex] = useState(-1)
  // A failed search says so, instead of a dropdown that never opens.
  const [searchError, setSearchError] = useState<string | null>(null)

  const dropdownRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  // Debounce search query
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedQuery(searchQuery)
    }, debounceMs)

    return () => clearTimeout(timer)
  }, [searchQuery, debounceMs])

  // Search employees from cached database (fast!)
  //
  // The dependencies matter here. This used to list `controlledSelected`, whose
  // default is a fresh `[]` on every render, so every answer set state, every
  // state change re-rendered, and every render asked again: the successor
  // search fired a request per frame until the tab was closed (Victor, 9 Sep
  // 2026 — 1 395 pending requests for one query). A call site that passes a
  // stable `selectedEmployees` was fine, which is why the feedback pickers
  // never showed it and the talent ones did.
  //
  // The selection is a display concern; it is filtered below, at render.
  useEffect(() => {
    let stale = false
    const fetchSuggestions = async () => {
      if (!debouncedQuery.trim() || debouncedQuery.length < 2) {
        setSuggestions([])
        setIsOpen(false)
        return
      }

      setIsLoading(true)
      setSearchError(null)
      setIsOpen(true)
      const fromFo = async (): Promise<Employee[]> => {
        // Search from cached employees (database query, very fast)
        const result = await integrationService.searchEmployees(
          debouncedQuery,
          20  // Fetch 20, display 10 after filtering
        )
        return (result?.employees ?? []).map(emp => ({
          id: emp.personnel_number,
          name: emp.full_name,
          role: emp.title_id || 'Team Member',
          department: emp.position_job_family_id || emp.office_location || 'Staff',
          campus: emp.office_location,
          email: emp.primary_contact_email
        }))
      }
      const fromPlatform = async (): Promise<Employee[]> => {
        const result = await talentReviewService.searchPeople(debouncedQuery, 20)
        return (result?.people ?? []).map(p => ({
          id: p.id,
          name: p.name,
          role: p.title || 'Team Member',
          department: p.department || p.campus || 'Staff',
          campus: p.campus || undefined,
          email: p.email || undefined
        }))
      }
      try {
        let found: Employee[]
        if (source === 'platform') {
          try {
            found = await fromPlatform()
          } catch (error) {
            console.warn('Platform directory search failed, trying the F&O search:', error)
            found = await fromFo()
          }
        } else {
          found = await fromFo()
        }
        if (stale) return   // a later keystroke has already answered
        setSuggestions(found)
        setIsOpen(true)
        setHighlightedIndex(-1)
      } catch (error) {
        console.error('Error searching employees:', error)
        setSuggestions([])
        setSearchError('The search did not answer. Check your connection and try again.')
        setIsOpen(true)
      } finally {
        setIsLoading(false)
      }
    }

    fetchSuggestions()
    // An answer that arrives after the next keystroke is thrown away, so a slow
    // one cannot overwrite a newer list.
    return () => { stale = true }
  }, [debouncedQuery, excludeUserId, source])

  /** What the dropdown shows: the answer, less anyone already chosen. */
  const visible = useMemo(
    () => suggestions
      .filter(emp => emp.id !== excludeUserId)
      .filter(emp => !controlledSelected.some(selected => selected.id === emp.id))
      .slice(0, 10),
    [suggestions, excludeUserId, controlledSelected],
  )

  // Click outside handler
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        inputRef.current &&
        !inputRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const handleSelectEmployee = useCallback((employee: Employee) => {
    const newSelected = [...controlledSelected, employee]
    onSelectionChange?.(newSelected)
    setSearchQuery('')
    setSuggestions([])
    setIsOpen(false)
    inputRef.current?.focus()
  }, [controlledSelected, onSelectionChange])

  const handleRemoveEmployee = useCallback((employeeId: string) => {
    const newSelected = controlledSelected.filter(emp => emp.id !== employeeId)
    onSelectionChange?.(newSelected)
  }, [controlledSelected, onSelectionChange])

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!isOpen) return

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault()
        setHighlightedIndex(prev =>
          prev < visible.length - 1 ? prev + 1 : prev
        )
        break
      case 'ArrowUp':
        e.preventDefault()
        setHighlightedIndex(prev => prev > 0 ? prev - 1 : -1)
        break
      case 'Enter':
        e.preventDefault()
        if (highlightedIndex >= 0 && highlightedIndex < visible.length) {
          handleSelectEmployee(visible[highlightedIndex])
        }
        break
      case 'Escape':
        setIsOpen(false)
        setHighlightedIndex(-1)
        break
    }
  }

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2)
  }

  return (
    <div className="w-full space-y-3">
      <div className="relative">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <Input
            ref={inputRef}
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyDown={handleKeyDown}
            onFocus={() => {
              if (visible.length > 0 && debouncedQuery.trim()) {
                setIsOpen(true)
              }
            }}
            placeholder={placeholder}
            className="pl-9 pr-4"
          />
        </div>

        {isOpen && (
          <div
            ref={dropdownRef}
            className="absolute z-50 w-full mt-2 bg-white border border-gray-200 rounded-lg shadow-lg max-h-80 overflow-auto"
          >
            {isLoading ? (
              <div className="p-4 text-center text-sm text-gray-600">
                {source === 'platform' ? 'Searching…' : 'Searching F&O...'}
              </div>
            ) : searchError ? (
              <div className="p-4 text-center text-sm text-red-700" role="alert">
                {searchError}
              </div>
            ) : visible.length === 0 ? (
              <div className="p-4 text-center text-sm text-gray-600">
                No one found for “{debouncedQuery}”
              </div>
            ) : (
              <div className="py-2">
                {visible.map((employee, index) => (
                  <button
                    key={employee.id}
                    onClick={() => handleSelectEmployee(employee)}
                    className={`w-full px-4 py-3 flex items-start gap-3 hover:bg-blue-50 transition-colors text-left ${
                      index === highlightedIndex ? 'bg-blue-50' : ''
                    }`}
                  >
                    <div className="flex-shrink-0 w-10 h-10 rounded-full bg-heritage-blue/10 flex items-center justify-center text-heritage-blue font-medium text-sm">
                      {getInitials(employee.name)}
                    </div>
                    <div className="flex-1">
                      <div className="font-medium text-gray-900">
                        {employee.name}
                      </div>
                      <div className="text-sm text-gray-600">
                        {employee.role}
                      </div>
                      <div className="flex items-center gap-2 mt-1 text-xs text-gray-500">
                        <span>{employee.department}</span>
                        {employee.campus && (
                          <>
                            <span>•</span>
                            <span>{employee.campus}</span>
                          </>
                        )}
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {controlledSelected.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {controlledSelected.map((employee) => (
            <Badge
              key={employee.id}
              variant="secondary"
              className="pl-2 pr-1 py-1.5 gap-2 text-sm bg-blue-50 text-blue-900 border-blue-200"
            >
              <div className="flex items-center gap-2">
                <div className="w-6 h-6 rounded-full bg-heritage-blue/10 flex items-center justify-center text-heritage-blue font-medium text-xs flex-shrink-0">
                  {getInitials(employee.name)}
                </div>
                <div className="flex flex-col items-start">
                  <span className="font-medium">{employee.name}</span>
                  <span className="text-xs text-gray-600">
                    {employee.role}
                  </span>
                </div>
              </div>
              <button
                onClick={() => handleRemoveEmployee(employee.id)}
                className="ml-1 rounded-full p-0.5 hover:bg-white/50 transition-colors"
                type="button"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </Badge>
          ))}
        </div>
      )}
    </div>
  )
}
